import styled from '@emotion/styled';
import { useRef } from 'react';
import { Content, GalleryItem, Season, VideoArtistAggregate } from '../../apiService';
import Carousel from '../../Carousel';
import ContentArtists from './ContentArtists';
import ContentGallery from './ContentGallery';
import ContentList from '../../ContentList';
import ContentSummary from './ContentSummary';
import { headerHeight } from '../../Header';
import { __t } from '../../i18n';
import SeriesContent from './SeriesContent';
import { Flex } from '../../styles';
import { sanitize } from '../../utils';
import useScrollTop from '../../utils/useScrollTop';

export default function ContentBody({ content, seasons, galleries, artists, bundle }: ContentBodyProps) {
  const { supportScroll, scrollToTop } = useScrollTop();

  const similarities = content.similarities?.configuration?.items;
  const { full: summary_html } = sanitize({ raw: content.summary || '' });

  const seriesContentRef = useRef<HTMLDivElement>(null);
  const handleScrollToEpisodes = () => {
    if (seriesContentRef?.current) {
      const top = seriesContentRef?.current?.offsetTop - parseInt(headerHeight);

      if (supportScroll) {
        window.scrollTo({ top, behavior: 'smooth' });
      } else {
        if (window.scrollY === top) {
          return;
        }

        scrollToTop(top);
      }
    }
  };

  const { season, episode } = content;
  const title = content?.series?.series_title || content.title;

  return (
    <>
      <SummaryGalleryWrapper direction={{ xs: 'column', md: 'row' }} mb={4}>
        <ContentSummary html={summary_html} />
        {galleries && <ContentGallery items={galleries} />}
      </SummaryGalleryWrapper>

      {!!seasons?.length && (
        <SeriesContent
          onScrollToEpisodes={handleScrollToEpisodes}
          seasons={seasons}
          season={season}
          episode={episode}
          ref={seriesContentRef}
        />
      )}
      {!!artists?.length && <ContentArtists artists={artists} />}
      {!!bundle?.length && <ContentList items={bundle} mb={4} />}

      {!!similarities?.length && !bundle?.length && (
        <Carousel title={__t('content.similarities') + ` ${title}`}>
          <ContentList items={similarities.map(({ video_content }) => video_content)} />
        </Carousel>
      )}
    </>
  );
}

const SummaryGalleryWrapper = styled(Flex)(({ theme }) => ({
  '> *': {
    [theme.breakpoints.up('md')]: {
      width: '50%',
    },
  },
}));

interface ContentBodyProps {
  content: Content;
  seasons?: Season[];
  artists: VideoArtistAggregate[];
  bundle?: Content[];
  galleries?: GalleryItem[];
}
